import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import ParentLayout from '../components/ParentLayout';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Users, Shield, ChevronRight, Search, Plus, UserCheck, Clock, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getDivisionLogo } from '../constants/divisionLogos';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminTeamsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDivision, setFilterDivision] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [teamsRes, divisionsRes] = await Promise.all([
        axios.get(`${API_URL}/api/teams`, { withCredentials: true }),
        axios.get(`${API_URL}/api/divisions`, { withCredentials: true })
      ]);
      
      // Enrich teams with roster/eligibility data
      const enrichedTeams = await Promise.all(teamsRes.data.map(async (team) => {
        try {
          // Get roster count from roster_assignments
          const rosterRes = await axios.get(`${API_URL}/api/coach/team/${team.team_id}/roster`, { 
            withCredentials: true 
          }).catch(() => ({ data: { roster: [], roster_count: 0 } }));
          
          return {
            ...team,
            roster_count: rosterRes.data?.roster_count || 0,
            cleared_count: rosterRes.data?.roster?.filter(p => p.clearance_status === 'cleared').length || 0,
            pending_count: rosterRes.data?.roster?.filter(p => p.clearance_status !== 'cleared').length || 0,
            waitlist_count: 0,
            division_name: divisionsRes.data.find(d => d.division_id === team.division_id)?.name || 'Unknown',
            coaches: []
          };
        } catch (e) {
          return {
            ...team,
            roster_count: 0,
            cleared_count: 0,
            pending_count: 0,
            waitlist_count: 0,
            division_name: divisionsRes.data.find(d => d.division_id === team.division_id)?.name || 'Unknown',
            coaches: []
          };
        }
      }));
      
      setTeams(enrichedTeams);
      setDivisions(divisionsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTeams = teams.filter(team => {
    const matchesSearch = team.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDivision = !filterDivision || team.division_id === filterDivision;
    return matchesSearch && matchesDivision;
  });

  const getCapacityColor = (count, capacity) => {
    const ratio = count / capacity;
    if (ratio >= 1) return 'text-red';
    if (ratio >= 0.8) return 'text-yellow-600';
    return 'text-green-600';
  };

  if (loading) {
    return (
      <ParentLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-12 bg-gray-200 rounded-lg w-1/3"></div>
            <div className="h-64 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      </ParentLayout>
    );
  }

  return (
    <ParentLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-teams-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-navy mb-1">Team Management</h1>
            <p className="text-gray-600">Manage teams, rosters, and assignments</p>
          </div>
          <Button className="mt-4 sm:mt-0 bg-navy hover:bg-navy/90">
            <Plus className="w-4 h-4 mr-2" />
            Add Team
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search teams..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="team-search-input"
            />
          </div>
          <select
            value={filterDivision}
            onChange={(e) => setFilterDivision(e.target.value)}
            className="px-4 py-2 border rounded-lg bg-white"
            data-testid="division-filter"
          >
            <option value="">All Divisions</option>
            {divisions.map(div => (
              <option key={div.division_id} value={div.division_id}>{div.name}</option>
            ))}
          </select>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-navy/10 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-navy" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-navy">{teams.length}</p>
                  <p className="text-xs text-gray-600">Total Teams</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <UserCheck className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-600">
                    {teams.reduce((sum, t) => sum + (t.cleared_count || 0), 0)}
                  </p>
                  <p className="text-xs text-gray-600">Cleared</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-yellow-600">
                    {teams.reduce((sum, t) => sum + (t.pending_count || 0), 0)}
                  </p>
                  <p className="text-xs text-gray-600">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-red/10 rounded-lg flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-red" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-red">
                    {teams.reduce((sum, t) => sum + (t.waitlist_count || 0), 0)}
                  </p>
                  <p className="text-xs text-gray-600">Waitlisted</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Teams List */}
        <div className="space-y-4">
          {filteredTeams.map(team => (
            <Card 
              key={team.team_id} 
              className="hover:border-navy/30 transition-colors cursor-pointer"
              onClick={() => navigate(`/admin/teams/${team.team_id}`)}
              data-testid={`team-card-${team.team_id}`}
            >
              <CardContent className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-14 h-14 flex items-center justify-center flex-shrink-0">
                      {(team.logo_url || getDivisionLogo(team.division_id)) ? (
                        <img 
                          src={team.logo_url || getDivisionLogo(team.division_id)} 
                          alt={`${team.name} logo`}
                          className="max-w-full max-h-full object-contain"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-navy rounded-lg flex items-center justify-center">
                          <Shield className="w-6 h-6 text-gold" />
                        </div>
                      )}
                    </div>
                    <div>
                      <h3 className="font-semibold text-navy text-lg">{team.name}</h3>
                      <p className="text-sm text-gray-600">{team.division_name}</p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {team.coaches?.slice(0, 2).map((coach, idx) => (
                          <Badge key={idx} variant="secondary" className="text-xs">
                            {coach.name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-6 mt-4 sm:mt-0">
                    <div className="text-center">
                      <p className={`text-2xl font-bold ${getCapacityColor(team.roster_count, team.roster_limit || team.roster_capacity)}`}>
                        {team.roster_count}/{team.roster_limit || team.roster_capacity || 25}
                      </p>
                      <p className="text-xs text-gray-600">Roster</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">{team.cleared_count || 0}</p>
                      <p className="text-xs text-gray-600">Cleared</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-yellow-600">{team.pending_count || 0}</p>
                      <p className="text-xs text-gray-600">Pending</p>
                    </div>
                    {team.waitlist_count > 0 && (
                      <div className="text-center">
                        <p className="text-2xl font-bold text-red">{team.waitlist_count}</p>
                        <p className="text-xs text-gray-600">Waitlist</p>
                      </div>
                    )}
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredTeams.length === 0 && (
          <div className="text-center py-12">
            <Shield className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No teams found</p>
          </div>
        )}
      </div>
    </ParentLayout>
  );
};

export default AdminTeamsPage;
